import { computed, makeObservable, runInAction } from 'mobx'

import { RouterOutput, client } from '@authy/lib-client/trpc'
import { WebAppState, WebAppStateInitParams } from '@authy/lib-web/appState'

import { NavigationItem, ApplicationKey } from './types'
import { getApplications } from './applications'
import { createCache } from './Cache'

export type TenantType = RouterOutput['tenants']['getTenant']

export type AdminAppStateInitParams = WebAppStateInitParams & {
    tenantShortId?: string
}

export class AdminAppState extends WebAppState<TenantType> {
    applicationKey: ApplicationKey
    cache = createCache()

    constructor(applicationKey: ApplicationKey) {
        super()

        this.applicationKey = applicationKey

        makeObservable(this, {
            mainNavigation: computed,
            applications: computed,
            application: computed,
        })
    }

    async init({ tenantShortId, ...otherParams }: AdminAppStateInitParams) {
        this.cache.init(this.application.key)

        if (tenantShortId) {
            this.cache.setTenantShortId(tenantShortId)
        }

        await super.init({
            ...otherParams,
        })

        if (tenantShortId) {
            await this.loadTenant({ tenantShortId: tenantShortId })
        }
    }

    get mainNavigation(): NavigationItem[] {
        throw new Error('Not implemented')
    }

    get applications() {
        return getApplications(this, { ignorePermissions: false })
    }

    get application() {
        const result = getApplications(this, { ignorePermissions: true }).find(({ key }) => key === this.applicationKey)

        if (!result) {
            throw new Error(`No application found for ${this.applicationKey}`)
        }

        return result
    }

    async loadTenant({ tenantShortId }: { tenantShortId: string }) {
        runInAction(() => {
            this.tenant = null
        })

        await this.cache.get(
            'tenant',
            () =>
                client.tenants.getTenant.query({
                    tenantId: tenantShortId,
                }),
            async (tenant) => {
                this.setTenant(tenant)
            }
        )

        if (!this.tenant) {
            throw new Error('missing.tenant')
        }
    }

    setTenant(tenant: TenantType | null) {
        super.setTenant(tenant)

        if (tenant) {
            this.cache.setTenantShortId(tenant.shortId)
        }
    }
}
